import {useState} from "react";
import type {ReviewerRole} from "platformed-client/types";
import {toaster} from "../../../components/ui/toaster";
import useTranslate from "platformed-components/i18n/useTranslate";
import {RemoveReviewersDialog} from "./RemoveReviewersDialog";
import {RoleReviewSections} from "./RoleReviewSections";
import type {ApplyOutcome, ApplyPlan, RemovalScope} from "./reviewCandidates";
import type {ReviewMap} from "./reviewsShared";
import {useReviewCommit} from "./useReviewCommit";
import type {ReviewItem} from ".";

/**
 * Role sections that write each change as it is made, for a surface where the user expects the
 * list in front of them to be the truth. Removals still stop for confirmation.
 */
export const ImmediateReviewSections = ({
  reviews,
  roles,
  reviewItem,
  targetName,
  descendantPageCount = 0,
}: {
  reviews: ReviewMap;
  roles: ReviewerRole[];
  reviewItem: ReviewItem;
  targetName: string;
  descendantPageCount?: number;
}) => {
  const t = useTranslate();
  const commit = useReviewCommit(reviewItem);
  /**
   * A plan waiting on the removal confirmation, with the picker's `onApply` promise still open.
   * Held outside the picker so cancelling returns the user to their draft. Settled exactly once:
   * whichever of confirm or dismiss lands first takes the decision.
   */
  const [pending, setPending] = useState<{plan: ApplyPlan; settle: (outcome: ApplyOutcome) => void} | null>(null);

  const settlePending = (outcome: ApplyOutcome) =>
    setPending(current => {
      current?.settle(outcome);
      return null;
    });

  // No success toast: the reviewer list redraws as soon as the write lands, so the change
  // confirms itself.
  const run = (plan: ApplyPlan, scope: RemovalScope) => commit([plan], scope);

  return (
    <>
      <RoleReviewSections
        reviews={reviews}
        roles={roles}
        reviewItem={reviewItem}
        descendantPageCount={descendantPageCount}
        onApply={async plan => {
          // Anyone being dropped has already been asked, so confirm first — and that is where
          // the user says how far the removal reaches. The picker waits on this promise.
          if (plan.removed.length > 0) {
            return new Promise<ApplyOutcome>(settle => setPending({plan, settle}));
          }
          try {
            await run(plan, "page");
          } catch (e) {
            toaster.error({title: t("tprm.reviews.toast.failed"), description: `${e}`});
            return "cancelled";
          }
          return "applied";
        }}
      />

      {pending && (
        <RemoveReviewersDialog
          open
          onClose={() => settlePending("cancelled")}
          targetName={targetName}
          removed={pending.plan.removed}
          addedCount={pending.plan.added.length}
          descendantPageCount={descendantPageCount}
          onConfirm={async scope => {
            await run(pending.plan, scope);
            settlePending("applied");
          }}
        />
      )}
    </>
  );
};
